import React, { useState } from 'react';
import {
  faSearch,
  faTimesCircle,
  faPlusCircle,
  faSave
} from '@fortawesome/pro-solid-svg-icons';
import { Form, FormInstance } from 'blueprint-form';
import { Container, Row, Col } from '../../../grid';
import Icon from '../../atoms/icon';
import Input from '../../molecules/input';
import Button from '../../molecules/button';
import Dialog from '../dialog';
import { CardFrame, CardTitle, CardBlock } from '../card';
import Modal from '../modal';
import Toast from '../toast';
import { useAuth } from '../../../contexts/auth';
import {
  ContactParams,
  CreateContact,
  ContactResponse,
  IContact
} from '../../../libs/contact';

import ContactForm, { GetContactFormInstance } from './form';
import { DefaultContactParams } from './types';

interface ContactSearchProps {
  className?: string;
  keyword?: string;
  setKeyword: React.Dispatch<React.SetStateAction<string>>;
  noCreate?: boolean;
  onCreated?: (contact: IContact) => any;
}

interface SearchData {
  term: string;
}

const ContactSearch: React.FC<ContactSearchProps> = ({
  className,
  keyword,
  setKeyword,
  noCreate,
  onCreated
}) => {
  const { isAdmin } = useAuth();
  if (!isAdmin) {
    noCreate = true;
  }
  const form = new FormInstance<SearchData>({
    term: ''
  });
  const onSubmit = (data: SearchData) => {
    setKeyword(data.term);
  };
  const clear = () => {
    setKeyword('');
    form.setData({
      term: ''
    });
  };
  const createForm = GetContactFormInstance(DefaultContactParams);
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const save = (params: ContactParams) => {
    setIsSaving(true);
    CreateContact(params).then((response) => {
      if (response.status !== 'OK') {
        Modal.error({
          title: 'Error Saving Contact',
          content: response.message
        });
        return;
      }
      if (onCreated) {
        onCreated((response as ContactResponse).contact);
      }
      Toast({
        intent: 'success',
        message: 'The contact has been added!',
        timeout: 2000
      });
      close();
    });
  };
  const close = (): void => {
    setIsCreateFormOpen(false);
  };
  return (
    <React.Fragment>
      <Form className={className} form={form} onSubmit={onSubmit}>
        <Container fluid style={{ paddingLeft: 0, paddingRight: 0 }}>
          <Row>
            <Col md={noCreate ? 18 : 12}>
              <Input
                name="term"
                placeholder="Search by keyword"
                aria-label="Search"
                rightElement={
                  keyword ? (
                    <Button
                      variant="minimal"
                      icon={<Icon icon={faTimesCircle} />}
                      onClick={clear}
                      aria-label="Clear Button"
                    />
                  ) : null
                }
              />
            </Col>
            <Col md={6}>
              <Button
                intent="primary"
                type="submit"
                fill
                icon={<Icon icon={faSearch} className="text-white" />}
              >
                Search
              </Button>
            </Col>
            {!noCreate && (
              <Col md={6}>
                <Button
                  intent="success"
                  fill
                  onClick={() => setIsCreateFormOpen(true)}
                >
                  <Icon icon={faPlusCircle} className="mr-4" /> Add Contact
                </Button>
              </Col>
            )}
          </Row>
        </Container>
      </Form>
      {!noCreate && (
        <Dialog
          style={{
            paddingBottom: 0
          }}
          maxWidth="1200px"
          isOpen={isCreateFormOpen}
          onClose={close}
        >
          <CardFrame>
            <CardTitle>
              <h2>Create Contact</h2>
            </CardTitle>
            <ContactForm form={createForm} onSubmit={save} />
            <CardBlock>
              <Button
                intent="success"
                className="mr-4"
                onClick={() => createForm.submit()}
                loading={isSaving}
              >
                <Icon className="mr-4" icon={faSave} /> Save Contact
              </Button>
              <Button onClick={close} disabled={isSaving}>
                <Icon icon={faTimesCircle} className="mr-4" /> Cancel Changes
              </Button>
            </CardBlock>
          </CardFrame>
        </Dialog>
      )}
    </React.Fragment>
  );
};

export default ContactSearch;
